// @ts-nocheck
import React, {useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import {AFF} from './affiliateTheme'
import {
  bookAffiliateMeeting,
  getMeetingAdmins,
  getMeetingCustomers,
  getMeetingSlots,
} from '../core/_requests'

const MEETING_TYPES = [
  {value: 'onboarding_new', label: 'אונבורדינג (לקוח חדש)'},
  {value: 'onboarding', label: 'אונבורדינג / הדרכה'},
  {value: 'technical', label: 'תמיכה טכנית'},
  {value: 'other', label: 'אחר'},
]

// The next two weeks of bookable days: no Saturday (the back office doesn't
// take meetings then), and no today once the 17:00 grid has run out.
const bookableDays = () => {
  const days = []
  const startsToday = moment().hour() < 17
  for (let i = startsToday ? 0 : 1; days.length < 14 && i < 28; i++) {
    const d = moment().add(i, 'days')
    if (d.day() === 6) continue
    days.push(d)
  }
  return days
}

// Books a meeting straight into the back office's meeting calendar: same
// 09:00–17:00 grid, same 20-minute gap rule, and the person gets the same
// WhatsApp confirmation an admin-booked meeting sends.
//
// `customer` is whoever the meeting is about: a referred customer ({_id}) or a
// lead that has no account yet ({lead_id}) — most meetings are the second kind,
// since the meeting is what turns a lead into a customer.
const AffiliateMeetingModal = ({customer, onClose, onBooked}) => {
  const [customers, setCustomers] = useState([])
  const [admins, setAdmins] = useState([])
  const [slots, setSlots] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const days = useMemo(bookableDays, [])
  const [form, setForm] = useState({
    // One value for the picker; `subjectKey` is 'acc:<id>' or 'lead:<id>'.
    subject: customer ? (customer.lead_id ? `lead:${customer.lead_id}` : `acc:${customer._id}`) : '',
    assigned_to: '',
    date: days[0].format('YYYY-MM-DD'),
    time: '',
    meeting_type: 'onboarding_new',
    notes: '',
  })

  useEffect(() => {
    getMeetingAdmins()
      .then((r) => {
        const list = r.data?.admins || []
        setAdmins(list)
        if (list.length) setForm((f) => (f.assigned_to ? f : {...f, assigned_to: list[0]._id}))
      })
      .catch(() => setError('טעינת רשימת הנציגים נכשלה'))
    // Only needed when the modal wasn't opened from a specific row.
    if (!customer) {
      getMeetingCustomers().then((r) => setCustomers(r.data || [])).catch(() => {})
    }
  }, [customer])

  // Slot availability is per admin per day, so re-fetch whenever either moves.
  useEffect(() => {
    if (!form.assigned_to || !form.date) return
    setLoadingSlots(true)
    getMeetingSlots(form.assigned_to, form.date)
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false))
  }, [form.assigned_to, form.date])

  // A slot picked for one admin/day may be taken on another — drop it.
  useEffect(() => {
    if (!form.time) return
    const s = slots.find((x) => x.time === form.time)
    if (s && (s.taken || s.past)) setForm((f) => ({...f, time: ''}))
  }, [slots]) // eslint-disable-line react-hooks/exhaustive-deps

  const subjectKey = (c) => (c.is_lead || c.lead_id ? `lead:${c.lead_id}` : `acc:${c._id}`)
  const selectedCustomer = customer || customers.find((c) => subjectKey(c) === form.subject)

  const submit = async () => {
    setError('')
    if (!form.subject) return setError('בחר לקוח או ליד')
    if (!form.assigned_to) return setError('בחר נציג')
    if (!form.time) return setError('בחר שעה')
    const [kind, id] = form.subject.split(':')
    setSaving(true)
    try {
      const res = await bookAffiliateMeeting({
        ...(kind === 'lead' ? {lead: id} : {customer: id}),
        assigned_to: form.assigned_to,
        meeting_date: `${form.date}T${form.time}`,
        meeting_type: form.meeting_type,
        notes: form.notes,
      })
      onBooked && onBooked(res.data?.meeting)
      onClose()
    } catch (e) {
      setError(e?.response?.data?.error || 'קביעת הפגישה נכשלה')
    }
    setSaving(false)
  }

  return (
    <div className='modal show d-block' tabIndex={-1} style={{background: 'rgba(0,0,0,0.5)'}} dir='rtl'>
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content' style={{borderRadius: 16, border: 'none'}}>
          <div className='modal-header border-0'>
            <h5 className='modal-title fw-bold'>
              קביעת פגישה{selectedCustomer ? ` · ${selectedCustomer.name || ''}` : ''}
            </h5>
            <button className='btn-close' onClick={onClose} />
          </div>

          <div className='modal-body pt-0'>
            <div style={{color: AFF.muted, fontSize: 13, marginBottom: 16}}>
              הפגישה נכנסת ליומן של צוות fbzipper, והלקוח/הליד מקבל הודעת אישור בוואטסאפ עם התאריך והשעה.
            </div>

            {!customer && (
              <div className='mb-3'>
                <label className='form-label fw-semibold'>לקוח / ליד</label>
                <select
                  className='form-select'
                  value={form.subject}
                  onChange={(e) => setForm({...form, subject: e.target.value})}
                >
                  <option value=''>בחר לקוח או ליד…</option>
                  {customers.map((c) => (
                    <option key={subjectKey(c)} value={subjectKey(c)}>
                      {c.is_lead ? 'ליד · ' : ''}
                      {c.name}
                      {c.phone ? ` · ${c.phone}` : ''}
                      {c.upcoming_meeting ? ` · פגישה קיימת ${moment(c.upcoming_meeting).format('DD/MM HH:mm')}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className='row g-3'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold'>נציג</label>
                <select
                  className='form-select'
                  value={form.assigned_to}
                  onChange={(e) => setForm({...form, assigned_to: e.target.value, time: ''})}
                >
                  {admins.map((a) => (
                    <option key={a._id} value={a._id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold'>סוג פגישה</label>
                <select
                  className='form-select'
                  value={form.meeting_type}
                  onChange={(e) => setForm({...form, meeting_type: e.target.value})}
                >
                  {MEETING_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className='mt-3'>
              <label className='form-label fw-semibold'>תאריך</label>
              <div style={{display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4}}>
                {days.map((d) => {
                  const key = d.format('YYYY-MM-DD')
                  const active = form.date === key
                  return (
                    <button
                      key={key}
                      type='button'
                      onClick={() => setForm({...form, date: key, time: ''})}
                      style={{
                        minWidth: 74,
                        border: `1px solid ${active ? AFF.accent : AFF.border}`,
                        background: active ? AFF.accentSoft : '#fff',
                        color: active ? AFF.accent : AFF.ink,
                        borderRadius: 10,
                        padding: '8px 6px',
                        fontWeight: 600,
                        fontSize: 12.5,
                        lineHeight: 1.4,
                      }}
                    >
                      <div>{d.format('dd')}</div>
                      <div>{d.format('DD/MM')}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className='mt-3'>
              <label className='form-label fw-semibold'>
                שעה {loadingSlots && <span className='spinner-border spinner-border-sm ms-2' />}
              </label>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                {slots.map((s) => {
                  const disabled = s.taken || s.past
                  const active = form.time === s.time
                  return (
                    <button
                      key={s.time}
                      type='button'
                      disabled={disabled}
                      title={s.taken ? 'תפוס' : s.past ? 'עבר' : ''}
                      onClick={() => setForm({...form, time: s.time})}
                      style={{
                        width: 68,
                        borderRadius: 8,
                        padding: '6px 0',
                        fontWeight: 600,
                        fontSize: 13,
                        border: `1px solid ${active ? AFF.accent : AFF.border}`,
                        background: active ? AFF.accent : disabled ? '#f5f5f7' : '#fff',
                        color: active ? '#fff' : disabled ? '#c0c2cd' : AFF.ink,
                        textDecoration: disabled ? 'line-through' : 'none',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {s.time}
                    </button>
                  )
                })}
                {!loadingSlots && slots.length === 0 && (
                  <div style={{color: AFF.muted, fontSize: 13}}>אין שעות זמינות ליום הזה.</div>
                )}
              </div>
            </div>

            <div className='mt-3'>
              <label className='form-label fw-semibold'>הערות לנציג (אופציונלי)</label>
              <textarea
                className='form-control'
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder='למשל: הלקוח מבקש שיחה בערב, יש לו 2 עמודים עסקיים…'
              />
            </div>

            {error && <div className='alert alert-danger mt-3 mb-0 py-2'>{error}</div>}
          </div>

          <div className='modal-footer border-0'>
            <button className='btn btn-light' onClick={onClose}>ביטול</button>
            <button className='btn btn-primary' onClick={submit} disabled={saving}>
              {saving ? <span className='spinner-border spinner-border-sm' /> : <><i className='bi bi-calendar-check me-1' /> קבע פגישה</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AffiliateMeetingModal
